import { ISelectOption } from '../../../ui-toolkit';
import { useSelectCurrentOrder } from '../../../selectors/order/selectCurrentOrder';
import { useMemo } from 'react';
import { translatePartiesToPeople } from '../../../types/signingAppointment/ISigningPersonData';
import { covertPartyPersonToSignerFormData, ISignerFormData } from '../../../types/signingAppointment/ISignerFormData';
import { ISigningAppointmentResponse } from '../../../types/signingAppointment/ISigningAppointmentResponse';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../types/shared/UUID';
import { useListThirdPartyOrderContactsQuery } from '../../../API/order/contacts/ThirdPartyContactsApi';
import { eCreateSigningAppointmentFormDataOwnerTypes } from '../../../types/order/consumer/eCreateSigningAppointmentFormDataOwnerTypes';
import { SelectOption } from '../../../tailwindComponents/selectTW/interfaces';
import { ePartyPurpose } from '../../../ui-toolkit/enums/ePartyPurpose';
import { ePartyType } from '../../../ui-toolkit/enums/ePartyType';
import { IPartySignatory } from '../../../ui-toolkit/orders/interfaces/IPartySignatory';
import { IParty, isBuyerBorrower } from '../../../ui-toolkit/orders/interfaces/IParty';
import { IPartyPerson } from '../../../ui-toolkit/orders/interfaces/IPartyPerson';

const findTransactionParties = (parties: IParty[], ownerType?: eCreateSigningAppointmentFormDataOwnerTypes) => {
  if (!ownerType) {
    return [];
  }

  const isSeller = ownerType.startsWith(eCreateSigningAppointmentFormDataOwnerTypes.seller);

  if (isSeller) {
    return parties.filter((party) => party.purpose === ePartyPurpose.seller);
  }

  return parties.filter(isBuyerBorrower);
};

export const filterExistingSigners = (
  signers: ISignerFormData[],
  existingAppointment: ISigningAppointmentResponse | undefined
): ISignerFormData[] => {
  if (!existingAppointment) return signers;

  return signers.filter((signer) => {
    if (signer.personId) {
      return !existingAppointment.signers?.some((existingSigner) => existingSigner.person?.id === signer.personId);
    }
    if (signer.signatoryId) {
      return !existingAppointment.signers?.some(
        (existingSigner) => existingSigner.signatory?.id === signer.signatoryId
      );
    }
    return true;
  });
};

const divestingCoOwnerFunctionId = 'divesting-co-owner';

// Options for signers are the buyer/borrower/seller and a divesting co-owner
export const useBuildSignerContacts = (
  ownerType?: eCreateSigningAppointmentFormDataOwnerTypes,
  existingAppointment?: ISigningAppointmentResponse
): ISelectOption[] => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { data: order } = useSelectCurrentOrder();
  const { data: thirdPartyContacts = [] } = useListThirdPartyOrderContactsQuery(orderId);
  return useMemo(() => {
    const transactionParties: IParty[] = findTransactionParties(order.parties, ownerType);

    const transactionPeople: ISignerFormData[] = translatePartiesToPeople(transactionParties).map((person) => {
      const val: ISignerFormData = {
        personId: person.personId,
        signatoryId: person.signatoryId,
        firstName: person.firstName,
        middleName: person.middleName,
        lastName: person.lastName,
        email: person.email,
        phoneNumber: person.phoneNumber,
      };
      return val;
    });

    const peopleOptions: ISelectOption[] = filterExistingSigners(transactionPeople, existingAppointment).map((p) => {
      return {
        label: `${p.firstName} ${p.lastName}`,
        value: p,
      };
    });

    const divestingCoOwners: ISelectOption[] = thirdPartyContacts
      .filter((contact) => contact.functions.includes(divestingCoOwnerFunctionId))
      .map((coOwner) => {
        const val: ISignerFormData = {
          firstName: coOwner.firstName,
          middleName: coOwner.middleName,
          lastName: coOwner.lastName,
          email: coOwner.emailAddress,
          phoneNumber: coOwner.phoneNumber,
        };
        return {
          label: `${coOwner.firstName} ${coOwner.lastName}`,
          value: val,
        };
      });

    return peopleOptions.concat(divestingCoOwners);
  }, [thirdPartyContacts, ownerType, order, existingAppointment]);
};

export const useBuildContactOptions2 = (
  ownerType?: eCreateSigningAppointmentFormDataOwnerTypes,
  existingAppointment?: ISigningAppointmentResponse
): { options: SelectOption[]; dictionary: Map<UUID, ISignerFormData> } => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { data: order } = useSelectCurrentOrder();
  const { data: thirdPartyContacts = [] } = useListThirdPartyOrderContactsQuery(orderId);
  const { options, dictionary } = useMemo(() => {
    const dictionary = new Map<UUID, ISignerFormData>();
    const options: SelectOption[] = [];

    findTransactionParties(order.parties, ownerType)
      .flatMap<IPartyPerson | IPartySignatory, 1>((transactionParty) => {
        if (transactionParty.type === ePartyType.person) {
          return transactionParty;
        }
        return transactionParty.signatories;
      })
      .map(covertPartyPersonToSignerFormData)
      // when this is a correction (i.e. existingAppointment is not undefined), show only the signers who were in the previous appointment
      .filter((signerFormData) => {
        if (!existingAppointment) return true;
        if (signerFormData.personId) {
          return !existingAppointment.signers?.some(
            (previousSigner) => previousSigner.person?.id === signerFormData.personId
          );
        }
        if (signerFormData.signatoryId) {
          return !existingAppointment.signers?.some(
            (previousSigner) => previousSigner.signatory?.id === signerFormData.signatoryId
          );
        }
        return true;
      })
      .forEach((signerFormData) => {
        dictionary.set(signerFormData.id!, signerFormData);
        options.push({ label: `${signerFormData.firstName} ${signerFormData.lastName}`, value: signerFormData.id! });
      });

    thirdPartyContacts
      .filter((contact) => contact.functions.includes(divestingCoOwnerFunctionId))
      .forEach((coOwner) => {
        const option = { label: `${coOwner.firstName} ${coOwner.lastName}`, value: coOwner.id };
        options.push(option);
        const entry: ISignerFormData = {
          firstName: coOwner.firstName,
          middleName: coOwner.middleName,
          lastName: coOwner.lastName,
          email: coOwner.emailAddress,
          phoneNumber: coOwner.phoneNumber,
        };
        dictionary.set(coOwner.id, entry);
      });

    return { options, dictionary };
  }, [thirdPartyContacts, ownerType, order, existingAppointment]);
  return { options, dictionary };
};
